import { createContext, useContext } from "react";

/** Where the hover pill sits on one row, or `null` for a row without it. */
export type NavPillPlace = {
  /** Counts entries into the list, so a new entry starts a new pill in place instead of sliding from the last row. */
  session: number;
  /** The mouse left the list, so the pill fades out on the row it last lit. */
  isOut: boolean;
  /** The pill's first row since the mouse entered the list, where it fades in. */
  isEntering: boolean;
};

/** The row under the pointer, kept outside React so a hover change renders only the two rows' pills, never the list. */
export class NavHoverStore {
  private rowId: string | null = null;
  private group: string | undefined;
  private place: NavPillPlace = { session: 0, isOut: false, isEntering: false };
  /** The pill left behind when the pointer crosses into another group, fading out on its row. */
  private fading: { rowId: string; place: NavPillPlace } | null = null;
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** A stable object per change, as `useSyncExternalStore` needs. */
  placeFor = (rowId: string) =>
    this.rowId === rowId
      ? this.place
      : this.fading?.rowId === rowId
        ? this.fading.place
        : null;

  /** The pill slides between rows of one group; crossing a group's label, it fades out and back in on the new row. */
  enter = (rowId: string, group: string | undefined) => {
    if (rowId === this.rowId && !this.place.isOut) return;
    const isNewPill =
      this.rowId === null || this.place.isOut || group !== this.group;
    if (isNewPill) {
      this.fading =
        this.rowId !== null && this.rowId !== rowId
          ? {
              rowId: this.rowId,
              place: { ...this.place, isOut: true, isEntering: false },
            }
          : null;
      this.place = {
        session: this.place.session + 1,
        isOut: false,
        isEntering: true,
      };
    } else {
      this.place = { ...this.place, isEntering: false };
    }
    this.rowId = rowId;
    this.group = group;
    this.emit();
  };

  leave = () => {
    if (this.rowId === null || this.place.isOut) return;
    this.place = { ...this.place, isOut: true, isEntering: false };
    this.emit();
  };

  private emit() {
    for (const listener of this.listeners) listener();
  }
}

export const NavHoverContext = createContext<NavHoverStore | null>(null);

/** The labeled group a row sits in, so the pill never slides across a group's label. */
export const NavHoverGroupContext = createContext<string | undefined>(
  undefined,
);

/** Whether the row sits in a list that shares one hover pill. */
export const useHasNavHoverPill = () => useContext(NavHoverContext) !== null;
